/**
 * CACFP Forms Page
 * Tracks CACFP Eligibility & Enrollment forms per child.
 * Director uploads blank form → sends secure link to parent → parent uploads completed form.
 * Separate from state licensing compliance — no impact on compliance score.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import AppShell from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Upload, Link2, Mail, Loader2, FileText, Download,
  CheckCircle2, AlertCircle, Clock, ChevronDown, ChevronUp, X, Trash2, Printer
} from "lucide-react";
import { format, addMonths, parseISO, isBefore } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CACFP_FORMS = [
  { id: "cacfp_eligibility", name: "CACFP Eligibility Form", renewalMonths: 12 },
  { id: "cacfp_enrollment", name: "CACFP Enrollment Form", renewalMonths: 12 },
];

const formatDate = (d) => {
  if (!d) return null;
  try {
    return format(parseISO(d.includes("T") ? d : d + "T12:00:00"), "MM/dd/yyyy");
  } catch {
    try { return format(new Date(d), "MM/dd/yyyy"); } catch { return d; }
  }
};

const getStatus = (record) => {
  if (!record?.lastSubmitted) return { label: "Not on file", color: "text-gray-400", bg: "" };
  if (record.notApplicable || record.lastSubmitted === "N/A") return { label: "N/A", color: "text-blue-600", bg: "bg-blue-50" };
  if (record.outstanding) return { label: "Outstanding", color: "text-amber-700", bg: "bg-amber-100" };
  if (record.expirationDate) {
    const exp = new Date(record.expirationDate + "T12:00:00");
    if (isBefore(exp, new Date())) return { label: "Expired", color: "text-red-700", bg: "bg-red-100" };
    const soon = new Date(); soon.setDate(soon.getDate() + 30);
    if (isBefore(exp, soon)) return { label: "Expiring Soon", color: "text-orange-700", bg: "bg-orange-100" };
  }
  return { label: "Current", color: "text-green-700", bg: "bg-green-100" };
};

export default function CACFPFormsPage() {
  const [children, setChildren] = useState([]);
  const [childRecords, setChildRecords] = useState({});
  const [formDocs, setFormDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [sendingLink, setSendingLink] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [confirmLink, setConfirmLink] = useState(null);
  const [savingDate, setSavingDate] = useState(null);
  const [bulkView, setBulkView] = useState(false);
  const [bulkDraft, setBulkDraft] = useState({});
  const [bulkPending, setBulkPending] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const templateUploadRef = useRef(null);
  const fileRefs = useRef({});

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [childRes, recordsRes] = await Promise.all([
        axios.get(`${API}/children`),
        axios.get(`${API}/children-records`),
      ]);
      const active = (childRes.data || []).filter(c => c.active !== false);
      setChildren(active);
      const rm = {};
      for (const rec of (recordsRes.data?.records || recordsRes.data || [])) {
        rm[rec.childId] = rec.forms || {};
      }
      setChildRecords(rm);
      initBulkDraft(rm, active);
    } catch { toast.error("Failed to load children"); }
    setLoading(false);
  };

  const initBulkDraft = (records, childList) => {
    const draft = {};
    childList.forEach(child => {
      draft[child.id] = {};
      CACFP_FORMS.forEach(form => {
        const rec = (records[child.id] || {})[form.id] || {};
        draft[child.id][form.id] = {
          date: (rec.lastSubmitted && rec.lastSubmitted !== "N/A") ? rec.lastSubmitted.slice(0, 10) : "",
          isNA: false,
          returnBy: rec.returnBy?.slice(0, 10) || "",
        };
      });
    });
    setBulkDraft(draft);
    setBulkPending({});
  };

  const handleBulkChange = (childId, formId, field, value) => {
    setBulkDraft(prev => ({
      ...prev,
      [childId]: { ...prev[childId], [formId]: { ...prev[childId]?.[formId], [field]: value } }
    }));
    setBulkPending(prev => ({ ...prev, [`${childId}-${formId}`]: true }));
  };

  const saveBulkChanges = async () => {
    const keys = Object.keys(bulkPending);
    if (!keys.length) { toast.info("No changes to save"); return; }
    setSavingBulk(true);
    let saved = 0, failed = 0;
    const childGroups = {};
    keys.forEach(k => {
      const [childId, formId] = k.split(/-(?=[^-]+$)/);
      if (!childGroups[childId]) childGroups[childId] = [];
      childGroups[childId].push(formId);
    });
    for (const [childId, formIds] of Object.entries(childGroups)) {
      try {
        const current = childRecords[childId] || {};
        const newForms = { ...current };
        formIds.forEach(formId => {
          const draft = bulkDraft[childId]?.[formId] || {};
          if (draft.isNA) {
            newForms[formId] = { ...current[formId], lastSubmitted: null, expirationDate: null, outstanding: false, notApplicable: true, returnBy: null };
          } else if (draft.date) {
            const form = CACFP_FORMS.find(f => f.id === formId);
            const exp = addMonths(new Date(draft.date + "T12:00:00"), form?.renewalMonths || 12);
            newForms[formId] = { ...current[formId], lastSubmitted: draft.date, expirationDate: format(exp, "yyyy-MM-dd"), outstanding: false, notApplicable: false, returnBy: draft.returnBy || null };
          } else {
            newForms[formId] = { ...current[formId], lastSubmitted: null, expirationDate: null, outstanding: false, notApplicable: false, returnBy: draft.returnBy || null };
          }
        });
        await axios.put(`${API}/children-records/${childId}`, { forms: newForms });
        setChildRecords(prev => ({ ...prev, [childId]: newForms }));
        saved++;
      } catch { failed++; }
    }
    setSavingBulk(false);
    setBulkPending({});
    if (saved) toast.success(`Saved ${saved} child${saved > 1 ? "ren" : ""}`);
    if (failed) toast.error(`Failed to save ${failed} child${failed > 1 ? "ren" : ""}`);
  };

  const downloadCACFPTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const response = await axios.get(`${API}/children-records/cacfp-bulk-template`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CACFP_Date_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch { toast.error("Failed to download template"); }
    setDownloadingTemplate(false);
  };

  const handleCACFPTemplateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }
    setUploadingTemplate(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/children-records/cacfp-bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message || `Updated ${response.data.childrenUpdated} children`);
      if (response.data.skippedDates?.length > 0) {
        response.data.skippedDates.forEach(msg => toast.error(msg, { duration: 10000 }));
      }
      if (response.data.conflicts?.length > 0) {
        response.data.conflicts.forEach(msg => toast.warning(`Overwrite: ${msg}`, { duration: 12000 }));
      }
      await fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to process upload"); }
    setUploadingTemplate(false);
    e.target.value = "";
  };

  const fetchChildDocs = async (childId) => {
    try {
      const res = await axios.get(`${API}/children-records/${childId}/form-documents`);
      const docs = res.data?.documents || {};
      setFormDocs(prev => {
        const next = { ...prev };
        for (const [formId, doc] of Object.entries(docs)) {
          next[`${childId}-${formId}`] = doc;
        }
        return next;
      });
    } catch { /* silently skip if no docs */ }
  };

  const saveDate = async (childId, formId, field, value) => {
    setSavingDate(`${childId}-${formId}-${field}`);
    try {
      const current = childRecords[childId] || {};
      const existing = current[formId] || {};
      const updated = { ...existing, [field]: value };
      if (field === "lastSubmitted" && value) {
        const form = CACFP_FORMS.find(f => f.id === formId);
        if (form?.renewalMonths) {
          const base = new Date(value + "T12:00:00");
          const exp = addMonths(base, form.renewalMonths);
          updated.expirationDate = format(exp, "yyyy-MM-dd");
        }
      }
      const newForms = { ...current, [formId]: updated };
      await axios.put(`${API}/children-records/${childId}`, { forms: newForms });
      setChildRecords(prev => ({ ...prev, [childId]: newForms }));
    } catch { toast.error("Failed to save date"); }
    setSavingDate(null);
  };

  const viewDoc = (doc) => {
    const bytes = atob(doc.data);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    window.open(URL.createObjectURL(new Blob([arr], { type: doc.contentType })), '_blank');
  };

  const downloadDoc = (doc, formName, childName) => {
    const bytes = atob(doc.data);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: doc.contentType }));
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.filename || `${formName}_${childName}.${doc.contentType.split('/')[1]}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUploadDoc = async (childId, formId, file) => {    if (!file) return;
    setUploadingDoc(`${childId}-${formId}`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("formId", formId);
      const res = await axios.post(`${API}/children-records/${childId}/upload-form-doc`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newDoc = res.data.document;
      setFormDocs(prev => ({ ...prev, [`${childId}-${formId}`]: newDoc }));
      toast.success("Document uploaded");
    } catch { toast.error("Upload failed"); }
    setUploadingDoc(null);
  };

  const handleSendLink = async () => {
    if (!confirmLink) return;
    const { childId, formId } = confirmLink;
    setSendingLink(`${childId}-${formId}`);
    setConfirmLink(null);
    try {
      const res = await axios.post(`${API}/children-records/${childId}/send-form-upload-links`, { formIds: [formId] });
      toast.success(res.data.message || "Secure upload link sent to parent!");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send link"); }
    setSendingLink(null);
  };

  const handleSendReminder = async (childId, formId, childName, formName) => {
    setSendingEmail(`${childId}-${formId}`);
    try {
      await axios.post(`${API}/children-records/${childId}/send-form-reminder`, { formId, formName });
      toast.success(`Reminder sent for ${formName}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send reminder"); }
    setSendingEmail(null);
  };

  const getChildEmail = (child) => child.pickupPersons?.find(p => p.email)?.email;

  const printCACFPReport = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const getStatusInfo = (rec) => {
      if (!rec?.lastSubmitted) return { label: 'Not on file', symbol: '✗', cls: 'missing' };
      if (rec.notApplicable || rec.lastSubmitted === 'N/A') return { label: 'N/A', symbol: '—', cls: 'na' };
      if (rec.outstanding) return { label: 'Outstanding', symbol: '⚠', cls: 'expiring' };
      if (rec.expirationDate) {
        const exp = new Date(rec.expirationDate + 'T12:00:00');
        if (exp < today) return { label: 'Expired', symbol: '✗', cls: 'expired' };
        const soon = new Date(); soon.setDate(soon.getDate() + 30);
        if (exp < soon) return { label: 'Expiring Soon', symbol: '⚠', cls: 'expiring' };
      }
      return { label: 'Current', symbol: '✓', cls: 'complete' };
    };

    const fmtDate = (d) => {
      if (!d) return '';
      try { return new Date(d + 'T12:00:00').toLocaleDateString('en-US'); } catch { return d; }
    };

    // Summary counts
    let totCurrent = 0, totExpiring = 0, totExpired = 0, totMissing = 0;
    children.forEach(child => {
      const recs = childRecords[child.id] || {};
      CACFP_FORMS.forEach(form => {
        const s = getStatusInfo(recs[form.id] || {});
        if (s.cls === 'complete') totCurrent++;
        else if (s.cls === 'expiring') totExpiring++;
        else if (s.cls === 'expired') totExpired++;
        else totMissing++;
      });
    });

    const rows = children.map(child => {
      const recs = childRecords[child.id] || {};
      const cells = CACFP_FORMS.map(form => {
        const rec = recs[form.id] || {};
        const s = getStatusInfo(rec);
        const dateReturned = fmtDate(rec.lastSubmitted);
        const expDate = fmtDate(rec.expirationDate);
        return `<td class="cell-${s.cls}">
          <span class="sym">${s.symbol}</span> ${s.label}
          ${dateReturned ? `<br/><small>Returned: ${dateReturned}</small>` : ''}
          ${expDate ? `<br/><small>Exp: ${expDate}</small>` : ''}
        </td>`;
      }).join('');
      return `<tr><td class="child-name">${child.name}</td>${cells}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>CACFP Forms Status Report</title>
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; color: #1a1a1a; margin: 20px; }
      h1 { font-size: 18px; color: #0f766e; margin: 0 0 2px; }
      .subtitle { font-size: 11px; color: #555; margin-bottom: 14px; }
      .summary { display: flex; gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
      .summary-box { padding: 8px 14px; border-radius: 6px; font-size: 11px; font-weight: bold; }
      .s-total { background: #f1f5f9; color: #334155; }
      .s-current { background: #dcfce7; color: #166534; }
      .s-expiring { background: #fef9c3; color: #854d0e; }
      .s-expired { background: #fee2e2; color: #991b1b; }
      .s-missing { background: #f3f4f6; color: #6b7280; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #0f766e; color: white; padding: 7px 10px; text-align: left; font-size: 11px; }
      td { padding: 6px 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; font-size: 11px; }
      td small { font-size: 10px; color: #555; }
      .child-name { font-weight: 600; color: #1e293b; white-space: nowrap; }
      .cell-complete { color: #166534; } .sym { font-weight: bold; }
      .cell-expiring { color: #854d0e; }
      .cell-expired { color: #991b1b; }
      .cell-missing { color: #6b7280; }
      .cell-na { color: #2563eb; }
      tr:nth-child(even) td { background: #f8fafc; }
      .note { font-size: 10px; color: #888; margin-top: 14px; border-top: 1px solid #e5e7eb; padding-top: 8px; }
      @media print { body { margin: 10px; } }
    </style></head><body>
    <h1>CACFP Forms Status Report</h1>
    <div class="subtitle">Generated: ${dateStr} &nbsp;|&nbsp; Total Children: ${children.length}</div>
    <div class="summary">
      <div class="summary-box s-current">✓ Current: ${totCurrent}</div>
      <div class="summary-box s-expiring">⚠ Expiring Soon: ${totExpiring}</div>
      <div class="summary-box s-expired">✗ Expired: ${totExpired}</div>
      <div class="summary-box s-missing">— Not on File: ${totMissing}</div>
    </div>
    <table>
      <thead><tr>
        <th>Child Name</th>
        ${CACFP_FORMS.map(f => `<th>${f.name}</th>`).join('')}
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="note">CACFP forms are tracked separately and do not count toward state licensing compliance scores.</div>
    </body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.print();
  };

  if (loading) return (
    <AppShell pageTitle="CACFP Forms">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
    </AppShell>
  );

  return (
    <AppShell pageTitle="CACFP Forms">
      <div className="max-w-5xl mx-auto space-y-4 px-2 py-4">

        {/* Header info */}
        <div className="bg-teal-50 border border-teal-200 rounded-xl px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-bold text-teal-800 text-base mb-1">CACFP Eligibility & Enrollment Forms</h2>
              <p className="text-sm text-teal-700">
                Upload the blank CACFP form for each child, then send a secure link for the parent to return the completed form.
                These forms are tracked separately — they do not count toward your state licensing compliance score.
              </p>
              <div className="flex items-center gap-4 mt-2 text-xs text-teal-600">
                <span className="flex items-center gap-1"><Upload className="w-3.5 h-3.5" /> Upload blank form template</span>
                <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Send email reminder</span>
                <span className="flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Send secure upload link to parent</span>
              </div>
            </div>
            <button
              onClick={printCACFPReport}
              data-testid="cacfp-print-report-btn"
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap flex-shrink-0"
            >
              <Printer className="w-4 h-4" /> Print Report
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => { setBulkView(false); }}
            data-testid="cacfp-individual-view-btn"
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${!bulkView ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >Individual Date Entry</button>
          <button
            onClick={() => { setBulkView(true); initBulkDraft(childRecords, children); }}
            data-testid="cacfp-bulk-view-btn"
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${bulkView ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >Bulk Date Entry</button>

          {bulkView && (
            <>
              <div className="h-5 w-px bg-gray-300 mx-1" />
              <button
                onClick={downloadCACFPTemplate}
                disabled={downloadingTemplate}
                data-testid="cacfp-download-template-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-teal-300 text-teal-700 bg-white hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-50"
              >
                {downloadingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Download Template
              </button>
              <label
                data-testid="cacfp-upload-template-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-teal-300 text-teal-700 bg-white hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
              >
                {uploadingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploadingTemplate ? "Uploading…" : "Upload Completed Template"}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  ref={templateUploadRef}
                  onChange={handleCACFPTemplateUpload}
                  disabled={uploadingTemplate}
                />
              </label>
            </>
          )}

          {bulkView && Object.keys(bulkPending).length > 0 && (
            <button
              onClick={saveBulkChanges}
              disabled={savingBulk}
              data-testid="cacfp-save-bulk-btn"
              className="ml-auto flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {savingBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save {Object.keys(bulkPending).length} Change{Object.keys(bulkPending).length > 1 ? "s" : ""}
            </button>
          )}
        </div>

        {/* Bulk Date Entry — Stacked Layout */}
        {bulkView && (
          <div className="space-y-3">
            {/* Column header */}
            <div className="hidden md:grid grid-cols-[1fr_140px_110px_140px_60px_90px] gap-2 px-4 py-2 bg-teal-50 border border-teal-100 rounded-lg text-xs font-semibold text-teal-700">
              <span>Form</span>
              <span>Date Returned</span>
              <span>Expires</span>
              <span>Return By</span>
              <span>N/A</span>
              <span>Status</span>
            </div>

            {children.map(child => (
              <div key={child.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {/* Child header */}
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                  <span className="font-semibold text-gray-800 text-sm">{child.name}</span>
                  {child.category && (
                    <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">{child.category}</span>
                  )}
                </div>

                {/* Form rows */}
                {CACFP_FORMS.map((form, formIdx) => {
                  const draft = bulkDraft[child.id]?.[form.id] || {};
                  const isPending = bulkPending[`${child.id}-${form.id}`];
                  const rec = (childRecords[child.id] || {})[form.id] || {};
                  const status = getStatus(rec);
                  const expiresDate = draft.date
                    ? format(addMonths(new Date(draft.date + "T12:00:00"), form.renewalMonths || 12), "MM/dd/yyyy")
                    : rec.expirationDate ? format(new Date(rec.expirationDate + "T12:00:00"), "MM/dd/yyyy") : null;

                  return (
                    <div
                      key={form.id}
                      className={`grid grid-cols-[1fr_140px_110px_140px_60px_90px] gap-2 items-center px-4 py-2.5 ${formIdx < CACFP_FORMS.length - 1 ? "border-b border-gray-100" : ""}`}
                    >
                      <span className="text-xs font-medium text-gray-600">{form.name}</span>

                      <input
                        type="date"
                        disabled={draft.isNA}
                        value={draft.isNA ? "" : (draft.date || "")}
                        onChange={e => handleBulkChange(child.id, form.id, "date", e.target.value)}
                        data-testid={`bulk-date-${child.id}-${form.id}`}
                        className="border border-gray-200 rounded px-2 py-1 text-xs disabled:bg-gray-100 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-teal-400 w-full"
                      />

                      <span className="text-xs text-gray-400 truncate">
                        {draft.isNA ? "—" : (expiresDate || <span className="italic text-gray-300">Auto-calc</span>)}
                      </span>

                      <input
                        type="date"
                        disabled={draft.isNA}
                        value={draft.isNA ? "" : (draft.returnBy || "")}
                        onChange={e => handleBulkChange(child.id, form.id, "returnBy", e.target.value)}
                        data-testid={`bulk-returnby-${child.id}-${form.id}`}
                        className="border border-gray-200 rounded px-2 py-1 text-xs disabled:bg-gray-100 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-teal-400 w-full"
                      />

                      <label className="flex items-center justify-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={draft.isNA || false}
                          onChange={e => {
                            handleBulkChange(child.id, form.id, "isNA", e.target.checked);
                            if (e.target.checked) handleBulkChange(child.id, form.id, "date", "");
                          }}
                          data-testid={`bulk-na-${child.id}-${form.id}`}
                          className="accent-blue-500"
                        />
                      </label>

                      <div>
                        {isPending
                          ? <span className="text-xs text-amber-600 font-medium">Unsaved</span>
                          : <span className={`text-xs px-1.5 py-0.5 rounded-full ${status.bg} ${status.color}`}>{status.label}</span>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Save button */}
            {Object.keys(bulkPending).length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={saveBulkChanges}
                  disabled={savingBulk}
                  data-testid="cacfp-save-bulk-bottom-btn"
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {savingBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Save {Object.keys(bulkPending).length} Change{Object.keys(bulkPending).length > 1 ? "s" : ""}
                </button>
              </div>
            )}

            {/* Summary counter */}
            <div className="border border-gray-200 bg-gray-50 rounded-xl px-4 py-3">
              <div className="flex flex-wrap gap-6">
                {CACFP_FORMS.map(form => {
                  const today = new Date();
                  let onFile = 0, na = 0, expired = 0, notOnFile = 0;
                  children.forEach(child => {
                    const rec = (childRecords[child.id] || {})[form.id] || {};
                    if (rec.notApplicable) { na++; }
                    else if (!rec.lastSubmitted || rec.lastSubmitted === "N/A") { notOnFile++; }
                    else if (rec.expirationDate && new Date(rec.expirationDate + "T12:00:00") < today) { expired++; }
                    else { onFile++; }
                  });
                  return (
                    <div key={form.id} className="flex items-center gap-4 text-xs">
                      <span className="font-semibold text-gray-600">{form.name}:</span>
                      <span className="text-green-700 font-medium" data-testid={`counter-onfile-${form.id}`}>On File: {onFile}</span>
                      <span className="text-blue-600 font-medium" data-testid={`counter-na-${form.id}`}>N/A: {na}</span>
                      <span className="text-red-600 font-medium" data-testid={`counter-expired-${form.id}`}>Expired: {expired}</span>
                      <span className="text-gray-400 font-medium" data-testid={`counter-notfile-${form.id}`}>Not on File: {notOnFile}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Children list — only shown in Individual View */}
        {!bulkView && (
          children.length === 0
            ? <div className="text-center py-10 text-gray-400">No enrolled children found.</div>
            : children.map(child => {
          const isExpanded = expanded[child.id];
          const records = childRecords[child.id] || {};
          const parentEmail = getChildEmail(child);

          return (
            <div key={child.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
              {/* Child header */}
              <button
                onClick={() => {
                  const nowExpanded = !expanded[child.id];
                  setExpanded(prev => ({ ...prev, [child.id]: nowExpanded }));
                  if (nowExpanded) fetchChildDocs(child.id);
                }}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">{child.name}</span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full capitalize">{child.category?.replace("_", " ")}</span>
                  {parentEmail && <span className="text-xs text-blue-500">{parentEmail}</span>}
                  {!parentEmail && <span className="text-xs text-amber-500 italic">No parent email on file</span>}
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>

              {/* Form rows */}
              {isExpanded && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {CACFP_FORMS.map(form => {
                    const rec = records[form.id] || {};
                    const docKey = `${child.id}-${form.id}`;
                    const doc = formDocs[docKey];
                    const status = getStatus(rec);
                    const isSaving = savingDate?.startsWith(`${child.id}-${form.id}`);
                    const isUploadingThis = uploadingDoc === docKey;
                    const isSendingLink = sendingLink === docKey;
                    const isSendingEmail = sendingEmail === docKey;

                    return (
                      <div key={form.id} className="px-5 py-4 bg-teal-50/30">
                        {/* Form title row */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {/* Upload blank form */}
                            <label className="cursor-pointer text-gray-400 hover:text-teal-600 transition-colors" title="Upload blank form for parent">
                              {isUploadingThis ? <Loader2 className="w-4 h-4 animate-spin text-teal-500" /> : <Upload className="w-4 h-4" />}
                              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                ref={el => fileRefs.current[docKey] = el}
                                onChange={e => { if (e.target.files[0]) handleUploadDoc(child.id, form.id, e.target.files[0]); e.target.value = ""; }}
                                disabled={isUploadingThis} />
                            </label>
                            {/* Email reminder */}
                            <button onClick={() => handleSendReminder(child.id, form.id, child.name, form.name)}
                              disabled={!parentEmail || isSendingEmail}
                              className="text-gray-400 hover:text-blue-500 transition-colors disabled:opacity-40" title="Send email reminder to parent">
                              {isSendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            </button>
                            {/* Send upload link */}
                            <button onClick={() => setConfirmLink({ childId: child.id, formId: form.id, childName: child.name, formName: form.name, parentEmail })}
                              disabled={!parentEmail || isSendingLink}
                              className="text-gray-400 hover:text-purple-500 transition-colors disabled:opacity-40" title="Send secure upload link to parent">
                              {isSendingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                            </button>
                            <span className="font-medium text-sm text-gray-800">{form.name}</span>
                            <span className="text-xs text-blue-600">(Yearly)</span>
                          </div>
                          {status.bg
                            ? <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.bg} ${status.color}`}>{status.label}</span>
                            : <span className={`text-xs ${status.color}`}>{status.label}</span>}
                        </div>

                        {/* Document area — shows uploaded doc or placeholder */}
                        <div className="mb-3">
                          {doc ? (
                            <div className="flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                              <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                              <span className="font-medium text-blue-700 text-xs truncate max-w-[160px]">{doc.filename || "Uploaded document"}</span>
                              {doc.uploadedBy === 'parent' && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Returned by parent</span>}
                              <div className="flex gap-2 ml-auto">
                                <button onClick={() => viewDoc(doc)} className="text-xs text-blue-600 hover:text-blue-800 font-semibold border border-blue-300 rounded px-2 py-0.5">View</button>
                                <button onClick={() => downloadDoc(doc, form.name, child.name)} className="text-xs text-green-600 hover:text-green-800 font-semibold border border-green-300 rounded px-2 py-0.5">Download</button>
                                <button onClick={() => viewDoc(doc)} className="text-xs text-purple-600 hover:text-purple-800 font-semibold border border-purple-300 rounded px-2 py-0.5">Print</button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-400">
                              <FileText className="w-4 h-4 flex-shrink-0" />
                              <span>Completed form from parent will appear here once uploaded</span>
                            </div>
                          )}
                        </div>

                        {/* Date fields */}
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            <label className="text-gray-500 text-xs">Date Returned:</label>
                            <input type="date" value={rec.lastSubmitted?.slice(0, 10) || ""}
                              onChange={e => saveDate(child.id, form.id, "lastSubmitted", e.target.value)}
                              className="border border-gray-300 rounded-lg px-2 py-1 text-xs h-8 focus:ring-1 focus:ring-teal-400 focus:border-teal-400" />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-gray-500 text-xs">Expires:</label>
                            {rec.expirationDate
                              ? <span className={`text-xs font-semibold px-2 py-0.5 rounded ${isBefore(new Date(rec.expirationDate + "T12:00:00"), new Date()) ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                                  {formatDate(rec.expirationDate)}
                                </span>
                              : <span className="text-xs text-gray-400">Auto-calculated</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-gray-500 text-xs">Return By:</label>
                            <input type="date" value={rec.returnBy?.slice(0, 10) || ""}
                              onChange={e => saveDate(child.id, form.id, "returnBy", e.target.value)}
                              className="border border-gray-300 rounded-lg px-2 py-1 text-xs h-8 focus:ring-1 focus:ring-teal-400 focus:border-teal-400" />
                          </div>
                          {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-teal-500" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
            })
        )}

        {/* Confirm send link dialog */}
        {confirmLink && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4">
              <h3 className="font-bold text-gray-900">Send Secure Upload Link</h3>
              <p className="text-sm text-gray-600">
                Send <strong>{confirmLink.parentEmail}</strong> a secure link to upload the completed
                <strong> {confirmLink.formName}</strong> for <strong>{confirmLink.childName}</strong>.
              </p>
              <p className="text-xs text-gray-400">The parent will receive an email with a secure link to upload their completed CACFP form directly to the child's record.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setConfirmLink(null)}>Cancel</Button>
                <Button className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSendLink}>Send Link</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
